/* THE BRILL MENU ON HER OWN WORDS (2026-11-03, docs/newsletter-write-first-spec.md).
 *
 * She writes the piece; Brill proposes, she accepts each part. Five tasks:
 * Fix spelling and grammar, Tighten (cuts only), Weave in my offer, Make it
 * search friendly, Ask Brill. Each is one call to /api/chat as newsletter_copy
 * with a [NEWSLETTER_WRITING] line, answered in a <<<NEWSLETTER_SUGGEST>>> block
 * of JSON (api/_lib/prompts.js, "WHEN SHE HAS WRITTEN IT HERSELF").
 *
 * HER WORDS ARE NEVER CHANGED BY THIS FILE WITHOUT HER PRESS, and never changed
 * by a suggestion that does not match her text EXACTLY. Every "find", "after"
 * and "before" Brill sends is checked here against what she has written NOW; a
 * paraphrase, a miss or a match in two places is dropped, never guessed at. A
 * "fix" that rewrites more than a fix should is dropped too: the prompt says
 * real mistakes only, and this is where that is held.
 */

import { buildMomentumContext } from "./momentumContext";
import { offerLine } from "./offerLine";

export const SHAPE_MODES = [
  { id: "fix", label: "Fix spelling and grammar", hint: "Real mistakes only. Your voice stays.", tokens: 1500 },
  { id: "tighten", label: "Tighten", hint: "Suggests cuts. Never rewrites.", tokens: 1200 },
  { id: "offer", label: "Weave in my offer", hint: "One or two spots, in Brill's words, yours to edit.", tokens: 1200 },
  { id: "search", label: "Make it search friendly", hint: "Title, short answer, subheads, claims, FAQ.", tokens: 2500 },
  { id: "ask", label: "Ask Brill…", hint: "Anything else, in your words.", tokens: 1500 },
];
export const shapeMode = (id) => SHAPE_MODES.find((m) => m.id === id) || SHAPE_MODES[0];

const TASK = {
  fix: "FIX",
  tighten: "TIGHTEN",
  offer: "OFFER",
  search: "SEARCH",
  ask: "ASK",
};

/** The [NEWSLETTER_WRITING] line: who she is writing to, her offer, her text. */
export function buildWritingContext({ newsletter, writing = {}, offer = null, pillars = [], mode, ask = "" }) {
  const lines = [
    "[NEWSLETTER_WRITING — her own words; read only, do not reference as a conversation]",
    `Task: ${TASK[mode] || "FIX"}`,
    `Type: Blog post (she wrote it herself)`,
    `Title: ${String(newsletter?.name || "").trim() || "(none yet)"}`,
  ];
  if (String(writing.subtitle || "").trim()) lines.push(`Subtitle: ${writing.subtitle.trim()}`);
  if (pillars.length) lines.push(`Her content pillars: ${pillars.join(" · ")}`);
  if (newsletter?.pillar) lines.push(`This piece's pillar: ${newsletter.pillar}`);
  /* One answer to what Brill is told about an offer: offerLine, as every
     other tool. Only the ONE she picked, never the ladder. */
  if (mode === "offer") lines.push(`The offer to weave in: ${offerLine(offer)}`);
  if (mode === "ask") lines.push(`What she asked: ${String(ask).trim().slice(0, 1500)}`);
  lines.push("", "Her piece, exactly as written:", "<<<HER_PIECE>>>", String(writing.text || ""), "<<<HER_PIECE_END>>>");
  return lines.join("\n");
}

const BLOCK = /<<<NEWSLETTER_SUGGEST>>>([\s\S]*?)<<<NEWSLETTER_SUGGEST_END>>>/;

/** Brill's reply → the JSON it carried, or null. Tolerates a code fence and
 *  chat before the block; never throws. */
export function parseShapeReply(reply) {
  const m = String(reply || "").match(BLOCK);
  if (!m) return null;
  const raw = m[1].trim().replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/, "").trim();
  try {
    const data = JSON.parse(raw);
    return data && typeof data === "object" ? data : null;
  } catch {
    return null;
  }
}

/* Where `find` sits in `text`: its index if it occurs EXACTLY ONCE, else -1. */
export function uniqueAt(text, find) {
  const f = String(find || "");
  if (!f.trim()) return -1;
  const i = String(text || "").indexOf(f);
  if (i < 0) return -1;
  return String(text).indexOf(f, i + 1) < 0 ? i : -1;
}

/* How much a fix changes: the share of characters that differ, roughly. A
   typo fix is small; a rewritten clause is not. */
function changeShare(a, b) {
  const x = String(a), y = String(b);
  let start = 0;
  while (start < x.length && start < y.length && x[start] === y[start]) start += 1;
  let end = 0;
  while (end < x.length - start && end < y.length - start && x[x.length - 1 - end] === y[y.length - 1 - end]) end += 1;
  const changed = Math.max(x.length - start - end, y.length - start - end);
  return changed / Math.max(x.length, 1);
}
const MAX_FIX_SHARE = 0.45;
const clipStr = (s, n) => String(s ?? "").trim().slice(0, n);
const list = (v) => (Array.isArray(v) ? v : []);

/**
 * Brill's JSON → only the suggestions that fit her text as it is now.
 * Each comes back with a stable `id` so the panel can mark it done.
 */
export function checkShape(mode, data, text) {
  const d = data || {};
  let n = 0;
  const id = () => `s${(n += 1)}`;
  if (mode === "fix") {
    return {
      fixes: list(d.fixes)
        .map((f) => ({ id: id(), find: String(f?.find ?? ""), replace: String(f?.replace ?? ""), why: clipStr(f?.why, 60) }))
        .filter((f) => f.find !== f.replace && uniqueAt(text, f.find) > -1 && changeShare(f.find, f.replace) <= MAX_FIX_SHARE)
        .slice(0, 30),
    };
  }
  if (mode === "tighten") {
    return {
      cuts: list(d.cuts)
        .map((c) => ({ id: id(), find: String(c?.find ?? ""), why: clipStr(c?.why, 120) }))
        .filter((c) => uniqueAt(text, c.find) > -1)
        .slice(0, 8),
    };
  }
  if (mode === "offer") {
    return {
      inserts: list(d.inserts)
        .map((x) => ({ id: id(), after: String(x?.after ?? ""), text: clipStr(x?.text, 400) }))
        .filter((x) => x.text && uniqueAt(text, x.after) > -1)
        .slice(0, 2),
      next: clipStr(d.next, 500),
    };
  }
  if (mode === "search") {
    const paras = String(text || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
    const first = paras[0] || "";
    return {
      titles: list(d.titles).map((t) => clipStr(t, 160)).filter(Boolean).slice(0, 3),
      answer: clipStr(d.answer, 700),
      /* A subhead goes ABOVE a paragraph, so "before" must be how one of her
         paragraphs starts, and never the first one. */
      subheads: list(d.subheads)
        .map((s) => ({ id: id(), before: String(s?.before ?? "").trim(), text: clipStr(s?.text, 120).replace(/^#+\s*/, "") }))
        .filter((s) => s.text && s.before && paras.some((p) => p !== first && p.startsWith(s.before)) && uniqueAt(text, s.before) > -1)
        .slice(0, 5),
      claims: list(d.claims).map((c) => ({ id: id(), text: clipStr(c, 300) })).filter((c) => c.text).slice(0, 5),
      faq: list(d.faq).map((f) => ({ id: id(), q: clipStr(f?.q, 200), a: clipStr(f?.a, 600) })).filter((f) => f.q && f.a).slice(0, 6),
      gaps: list(d.gaps).map((g) => ({ id: id(), q: clipStr(g?.q, 200), why: clipStr(g?.why, 200) })).filter((g) => g.q).slice(0, 3),
      pillar: clipStr(d.pillar, 120),
    };
  }
  return {
    reply: clipStr(d.reply, 1500),
    edits: list(d.edits)
      .map((e) => ({ id: id(), find: String(e?.find ?? ""), replace: String(e?.replace ?? ""), why: clipStr(e?.why, 120) }))
      .filter((e) => e.find !== e.replace && uniqueAt(text, e.find) > -1)
      .slice(0, 10),
  };
}

/* ── Applying what she accepts. Each returns the new text, or null when the
   spot has changed since Brill looked — the panel says so rather than guess. */

export function applyReplace(text, find, replace) {
  const i = uniqueAt(text, find);
  if (i < 0) return null;
  return text.slice(0, i) + replace + text.slice(i + find.length);
}

/** A cut: the words go, and the space they leave is tidied. */
export function applyCut(text, find) {
  const i = uniqueAt(text, find);
  if (i < 0) return null;
  let before = text.slice(0, i), after = text.slice(i + find.length);
  if (/ $/.test(before) && (after === "" || /^[ ,.;:!?\n]/.test(after))) before = before.slice(0, -1);
  else if (/^ /.test(after) && (before === "" || /[\n(]$/.test(before))) after = after.slice(1);
  return before + after;
}

/** Brill's sentence goes in right after hers, in the same paragraph. */
export function applyInsert(text, after, sentence) {
  const i = uniqueAt(text, after);
  if (i < 0) return null;
  const end = i + after.length;
  return `${text.slice(0, end)} ${String(sentence).trim()}${text.slice(end)}`;
}

/** A subhead goes in as its own "## " paragraph above the paragraph that
 *  starts with `before`. Already there: unchanged. */
export function applySubhead(text, before, heading) {
  const line = `## ${String(heading).trim()}`;
  if (text.includes(`${line}\n`)) return text;
  const i = uniqueAt(text, before);
  if (i < 0) return null;
  const start = text.lastIndexOf("\n", i - 1) + 1;
  if (text.slice(start, i).trim()) return null;   // not the start of a paragraph
  return `${text.slice(0, start)}${line}\n\n${text.slice(start)}`;
}

/** What a change actually changes, with a few words either side — so a fix
 *  that adds one comma reads as one comma, not a struck-through sentence. */
export function changeView(find, replace, around = 24) {
  const a = String(find), b = String(replace);
  let s = 0;
  while (s < a.length && s < b.length && a[s] === b[s]) s += 1;
  let e = 0;
  while (e < a.length - s && e < b.length - s && a[a.length - 1 - e] === b[b.length - 1 - e]) e += 1;
  const lead = a.slice(Math.max(0, s - around), s);
  const tail = a.slice(a.length - e, a.length - e + around);
  return {
    before: `${s > around ? "…" : ""}${lead}`,
    was: a.slice(s, a.length - e),
    now: b.slice(s, b.length - e),
    after: `${tail}${a.length - e + around < a.length ? "…" : ""}`,
  };
}

/** The FAQ block's text, the way the blog reads it: a question line, its answer under it. */
export const faqText = (faq) => faq.map((f) => `${f.q}\n${f.a}`).join("\n\n");
export const claimsText = (claims) => claims.map((c) => c.text).join("\n");

/** One Brill call for one task. Returns the reply text; throws a plain message. */
export async function newsletterShapeReply({ token, chatContext = {}, newsletter, writing, offer, pillars, mode, ask }) {
  const ctx = buildMomentumContext({
    moduleId: "newsletter_copy",
    acceleratorOutputs: chatContext.acceleratorOutputs,
    activeNiche: chatContext.activeNiche,
    isStale: (sid, name) => {
      const stamp = chatContext.outputStamps?.[sid]?.[name];
      return !!(chatContext.activeNiche && stamp && stamp !== chatContext.activeNiche.id);
    },
  });
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      moduleId: "newsletter_copy",
      max_tokens: shapeMode(mode).tokens,
      moduleLabel: "Blog post",
      archetype: chatContext.userSettings?.archetype || null,
      archetypePair: chatContext.userSettings?.archetypePair || null,
      messages: [
        ...(ctx ? [{ role: "user", content: ctx }] : []),
        { role: "user", content: buildWritingContext({ newsletter, writing, offer, pillars, mode, ask }) },
        { role: "user", content: `${shapeMode(mode).label}. Answer with the block.` },
      ],
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "Brill didn't answer just then. Your piece wasn't touched; try again in a moment.");
  return data.content?.find((b) => b.type === "text")?.text || "";
}
